import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { MarketProvider } from "@/contexts/MarketContext";
import { CartProvider, useCart } from "@/contexts/CartContext";
import { StickyCartProvider } from "@/contexts/StickyCartContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { ChatProvider } from "@/contexts/ChatContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ScrollToTopOnNav } from "@/components/ScrollToTopOnNav";
import { GlobalChatbot } from "@/components/GlobalChatbot";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";

import { useEffect, lazy, Suspense, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { usePageTracking } from "@/hooks/use-page-tracking";

// Critical pages - load immediately
import { VariantDEditorial } from "./pages/landing-variants/VariantD-Editorial";

// Lazy-loaded pages - defer loading until needed
const Products = lazy(() => import("./pages/Products"));
const Product = lazy(() => import("./pages/Product"));
const Favorites = lazy(() => import("./pages/Favorites"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCanceled = lazy(() => import("./pages/PaymentCanceled"));
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Admin pages - lazy loaded
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminConversations = lazy(() => import("./pages/AdminConversations"));
const AdminOMSOrders = lazy(() => import("./pages/AdminOMSOrders"));
const AdminOMSSettings = lazy(() => import("./pages/AdminOMSSettings"));
const AdminOrderSettings = lazy(() => import("./pages/AdminOrderSettings"));
const AdminMarketingSettings = lazy(() => import("./pages/AdminMarketingSettings"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminJWTTest = lazy(() => import("./pages/AdminJWTTest"));
const AdminOutboxEvents = lazy(() => import("./pages/AdminOutboxEvents"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminCookieConsent = lazy(() => import("./pages/AdminCookieConsent"));

// Public pages - lazy loaded
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const Brands = lazy(() => import("./pages/Brands"));
const Brand = lazy(() => import("./pages/Brand"));

const CustomSupplements = lazy(() => import("./pages/CustomSupplements"));

// Footer pages - lazy loaded
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Careers = lazy(() => import("./pages/Careers"));
const Press = lazy(() => import("./pages/Press"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Shipping = lazy(() => import("./pages/Shipping"));
const Returns = lazy(() => import("./pages/Returns"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Compliance = lazy(() => import("./pages/Compliance"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Page tracking component
const PageTracker = () => {
  usePageTracking();
  return null;
};

const FaviconUpdater = () => {
  const location = useLocation();

  useEffect(() => {
    const updateSiteMetadata = async () => {
      try {
        const { data, error } = await supabase
          .from('site_settings')
          .select('favicon_url, site_name, header_logo_url, site_url')
          .limit(1)
          .single();

        if (error && error.code !== 'PGRST116') throw error;
        
        // Update favicon
        if (data?.favicon_url) {
          const existingLinks = document.querySelectorAll("link[rel*='icon']");
          existingLinks.forEach(link => link.remove());

          const link = document.createElement('link');
          link.rel = 'icon';
          link.href = data.favicon_url;
          link.type = 'image/x-icon';
          document.head.appendChild(link);
        }

        // Update page title
        if (data?.site_name) {
          document.title = data.site_name;
          
          // Update meta description
          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc) {
            metaDesc.setAttribute('content', `Discover premium health supplements at ${data.site_name}. Expert support and trusted quality.`);
          }

          // Update Open Graph tags
          const ogTitle = document.querySelector('meta[property="og:title"]');
          if (ogTitle) ogTitle.setAttribute('content', data.site_name);
          
          const ogDesc = document.querySelector('meta[property="og:description"]');
          if (ogDesc) {
            ogDesc.setAttribute('content', `Discover premium health supplements at ${data.site_name}. Expert support and trusted quality.`);
          }

          const ogSiteName = document.querySelector('meta[property="og:site_name"]');
          if (ogSiteName) ogSiteName.setAttribute('content', data.site_name);

          // Update Twitter tags
          const twitterTitle = document.querySelector('meta[name="twitter:title"]');
          if (twitterTitle) twitterTitle.setAttribute('content', data.site_name);
          
          const twitterDesc = document.querySelector('meta[name="twitter:description"]');
          if (twitterDesc) {
            twitterDesc.setAttribute('content', `Discover premium health supplements at ${data.site_name}. Expert support and trusted quality.`);
          }
        }

        // Update social sharing images
        if (data?.header_logo_url) {
          const ogImage = document.querySelector('meta[property="og:image"]');
          if (ogImage) ogImage.setAttribute('content', data.header_logo_url);
          
          const twitterImage = document.querySelector('meta[name="twitter:image"]');
          if (twitterImage) twitterImage.setAttribute('content', data.header_logo_url);
        }

        // Update canonical and URL meta tags
        if (data?.site_url) {
          const fullUrl = `${data.site_url}${location.pathname}`;
          
          // Update or create canonical link
          let canonical = document.querySelector('link[rel="canonical"]');
          if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
          }
          canonical.setAttribute('href', fullUrl);

          // Update Open Graph URL
          const ogUrl = document.querySelector('meta[property="og:url"]');
          if (ogUrl) ogUrl.setAttribute('content', fullUrl);

          // Update Twitter URL
          const twitterUrl = document.querySelector('meta[name="twitter:url"]');
          if (twitterUrl) twitterUrl.setAttribute('content', fullUrl);
        }
      } catch (error) {
        console.error('Error updating site metadata:', error);
      }
    };

    updateSiteMetadata();
  }, [location.pathname]);

  return null;
};

// Wrapper component to connect MarketProvider with CartContext
const MarketProviderWithCart = ({ children }: { children: React.ReactNode }) => {
  const { clearCart } = useCart();
  
  const handleClearCart = useCallback(() => {
    clearCart();
  }, [clearCart]);

  return (
    <MarketProvider onClearCart={handleClearCart}>
      {children}
    </MarketProvider>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <CartProvider>
      <MarketProviderWithCart>
        <StickyCartProvider>
          <FavoritesProvider>
            <ChatProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <FaviconUpdater />
                  <PageTracker />
                  <ScrollToTopOnNav />
                  <ScrollToTop />
                  <GlobalChatbot />
                  <CookieConsentBanner />
                  
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<VariantDEditorial />} />
                      <Route path="/products" element={<Products />} />
                      <Route path="/favorites" element={<Favorites />} />
                      <Route path="/cart" element={<Cart />} />
                      <Route path="/checkout" element={<Checkout />} />
                      <Route path="/order-success" element={<OrderSuccess />} />
                      <Route path="/payment-success" element={<PaymentSuccess />} />
                      <Route path="/payment-canceled" element={<PaymentCanceled />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      
                      {/* Admin Routes */}
                      <Route path="/admin" element={<AdminDashboard />} />
                      <Route path="/admin/settings" element={<AdminSettings />} />
                      <Route path="/admin/oms-orders" element={<AdminOMSOrders />} />
                      <Route path="/admin/oms-settings" element={<AdminOMSSettings />} />
                      <Route path="/admin/order-settings" element={<AdminOrderSettings />} />
                      <Route path="/admin/marketing-settings" element={<AdminMarketingSettings />} />
                      <Route path="/admin/conversations" element={<AdminConversations />} />
                      <Route path="/admin/users" element={<AdminUsers />} />
                      <Route path="/admin/jwt-test" element={<AdminJWTTest />} />
                      <Route path="/admin/outbox-events" element={<AdminOutboxEvents />} />
                      <Route path="/admin/analytics" element={<AdminAnalytics />} />
                      <Route path="/admin/cookie-consent" element={<AdminCookieConsent />} />
                      
                      {/* Public Product Pages */}
                      <Route path="/category/:slug" element={<CategoryPage />} />
                      <Route path="/brands" element={<Brands />} />
                      <Route path="/brand/:slug" element={<Brand />} />
                      <Route path="/product/:slug" element={<Product />} />
                      
                      
                      <Route path="/custom-supplements" element={<CustomSupplements />} />
                      
                      {/* Footer pages */}
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/careers" element={<Careers />} />
                      <Route path="/press" element={<Press />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/shipping" element={<Shipping />} />
                      <Route path="/returns" element={<Returns />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/cookies" element={<Cookies />} />
                      <Route path="/compliance" element={<Compliance />} />
                      
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              </TooltipProvider>
            </ChatProvider>
          </FavoritesProvider>
        </StickyCartProvider>
      </MarketProviderWithCart>
    </CartProvider>
  </QueryClientProvider>
);

export default App;
